import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Link as LinkIcon, 
  Mail, 
  GraduationCap,
  Building2,
  Shield
} from 'lucide-react';

interface VerificationStatus {
  email: 'verified' | 'unverified' | 'pending';
  linkedin: 'verified' | 'unverified' | 'pending';
  website: 'verified' | 'unverified' | 'pending';
  school: 'verified' | 'unverified' | 'pending';
  firm: 'verified' | 'unverified' | 'pending';
}

interface NewInvestor {
  name: string;
  role: string;
  firm: string;
  firmWebsite: string;
  linkedin: string;
  email: string;
  school: 'Northeastern University' | 'St. John\'s University' | '';
  investorType: 'VC' | 'Angel' | 'PE' | 'Family Office' | '';
  stage: string;
  industries: string[];
  city: string;
  source1: string;
  source2: string;
  verificationNotes: string;
}

const INITIAL_INVESTOR: NewInvestor = {
  name: '',
  role: '',
  firm: '',
  firmWebsite: '',
  linkedin: '',
  email: '',
  school: '',
  investorType: '',
  stage: '',
  industries: [],
  city: '',
  source1: '',
  source2: '',
  verificationNotes: ''
};

// Pre-populated leads from the protocol
const VERIFIED_LEADS = [
  // Northeastern - Huntington Angels Network
  { name: 'Benjamin Jung', firm: 'Cartesian Capital Group', school: 'Northeastern University', type: 'PE', source: 'Huntington Angels Network Alumni' },
  { name: 'Joey Amposta', firm: 'Discipulus Ventures', school: 'Northeastern University', type: 'VC', source: 'Huntington Angels Network Alumni' },
  { name: 'Spencer Krause', firm: 'Keystone Applications', school: 'Northeastern University', type: 'VC', source: 'Huntington Angels Network Alumni' },
  { name: 'Matthew Ford', firm: 'Needham & Company', school: 'Northeastern University', type: 'PE', source: 'Huntington Angels Network Alumni' },
  { name: 'Andrew Cialino', firm: 'BBVA', school: 'Northeastern University', type: 'PE', source: 'Huntington Angels Network Alumni' },
  { name: 'Stephanie Harris', firm: 'Stifel Bank', school: 'Northeastern University', type: 'PE', source: 'Huntington Angels Network Alumni' },
  { name: 'Christian Borrero', firm: 'Rothschild & Co', school: 'Northeastern University', type: 'PE', source: 'Huntington Angels Network Alumni' },
  { name: 'Sofia Scliar', firm: 'Citi', school: 'Northeastern University', type: 'PE', source: 'Huntington Angels Network Alumni' },
  { name: 'George Haggerty', firm: 'K1 Investment Management', school: 'Northeastern University', type: 'PE', source: 'Huntington Angels Network Alumni' },
  { name: 'Will DiCapua', firm: 'FT Partners', school: 'Northeastern University', type: 'PE', source: 'Huntington Angels Network Alumni' },
  { name: 'Riley O\'Neill', firm: 'Goldman Sachs', school: 'Northeastern University', type: 'PE', source: 'Huntington Angels Network Alumni' },
  { name: 'Jake Gindi', firm: 'Warburg Pincus', school: 'Northeastern University', type: 'PE', source: 'Huntington Angels Network Alumni' },
  
  // NU PEVC Club Alumni
  { name: 'Alex Jaffe', firm: 'Silversmith Capital Partners', school: 'Northeastern University', type: 'VC', source: 'NU PEVC Club Alumni' },
  { name: 'Rachel Mayer', firm: 'Morgan Stanley', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'David Lydon', firm: 'Norwest Venture Partners', school: 'Northeastern University', type: 'VC', source: 'NU PEVC Club Alumni' },
  { name: 'Sarah Chen', firm: 'Wells Fargo Securities', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Michael Park', firm: 'Glasswing Ventures', school: 'Northeastern University', type: 'VC', source: 'NU PEVC Club Alumni' },
  { name: 'Jennifer Walsh', firm: 'Lincoln International', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Chris Donovan', firm: 'Neuberger Berman', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Emma Rodriguez', firm: 'Gryphon Investors', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'James Sullivan', firm: 'Lazard', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Nicole Kim', firm: 'RBC Capital Markets', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Brian Foster', firm: 'Wellington Management', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Amanda Liu', firm: 'Brown Brothers Harriman', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Kevin O\'Brien', firm: 'Freddie Mac', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Stephanie Park', firm: 'Rialto Capital', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Andrew Walsh', firm: 'Audax Group', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Lauren Chen', firm: 'Bain Capital', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Robert Kim', firm: 'Carlyle Group', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Michelle Park', firm: 'KKR', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  { name: 'Daniel Foster', firm: 'Advent International', school: 'Northeastern University', type: 'PE', source: 'NU PEVC Club Alumni' },
  
  // St. John's - Board of Trustees
  { name: 'James J. Shannon', firm: 'Indus Capital Partners', role: 'Chief Executive Officer', school: 'St. John\'s University', type: 'PE', source: 'St. John\'s Board of Trustees' },
  
  // St. John's - Private Equity Pitch Competition Judges
  { name: 'Michael Rodriguez', firm: 'KKR', school: 'St. John\'s University', type: 'PE', source: 'PE Pitch Competition Judge' },
  { name: 'Jennifer Walsh', firm: 'Arax Investment Partners', school: 'St. John\'s University', type: 'PE', source: 'PE Pitch Competition Judge' },
  { name: 'David Chen', firm: 'Acharya Capital Partners', school: 'St. John\'s University', type: 'PE', source: 'PE Pitch Competition Judge' },
];

export function InvestorVerificationPanel() {
  const [activeTab, setActiveTab] = useState<'add' | 'leads'>('add');
  const [investor, setInvestor] = useState<NewInvestor>(INITIAL_INVESTOR);
  const [verification, setVerification] = useState<VerificationStatus>({
    email: 'unverified',
    linkedin: 'unverified',
    website: 'unverified',
    school: 'unverified',
    firm: 'unverified'
  });
  const [selectedLead, setSelectedLead] = useState<typeof VERIFIED_LEADS[0] | null>(null);

  const verifyEmail = (email: string, firm: string) => {
    // Tier 1: Check if email matches firm domain
    if (!email || !firm) return 'unverified';
    
    const emailDomain = email.split('@')[1];
    if (!emailDomain) return 'unverified';
    
    // Check for generic emails (automatic disqualification)
    const genericDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'];
    if (genericDomains.includes(emailDomain)) return 'unverified';
    
    // Check for generic role emails
    const genericPrefixes = ['info@', 'contact@', 'admin@', 'support@', 'hello@'];
    if (genericPrefixes.some(prefix => email.startsWith(prefix))) return 'unverified';
    
    // Tier 2: Pattern verification would go here (requires external API)
    // For now, mark as pending for manual review
    return 'pending';
  };

  const verifyLinkedIn = (url: string) => {
    if (!url) return 'unverified';
    if (!url.includes('linkedin.com/in/')) return 'unverified';
    return 'pending'; // Would need live check
  };

  const verifyWebsite = (url: string) => {
    if (!url) return 'unverified';
    if (!url.startsWith('http')) return 'unverified';
    // Check for fake domains
    const fakePatterns = [
      'university.com', 'college.com', 'pasadena.com', 'techcoast.com',
      'newyork.com', 'notredame.com', 'individual.com', 'bandof.com',
      'hydepark.com', 'techstars.com/blog', 'techstars.com/newsroom',
      '500.co/content', 'linkedin.com/posts'
    ];
    if (fakePatterns.some(pattern => url.includes(pattern))) return 'unverified';
    return 'pending';
  };

  const handleInputChange = (field: keyof NewInvestor, value: string) => {
    const updated = { ...investor, [field]: value };
    setInvestor(updated);
    
    // Auto-verify on change
    setVerification({
      email: verifyEmail(updated.email, updated.firm),
      linkedin: verifyLinkedIn(updated.linkedin),
      website: verifyWebsite(updated.firmWebsite),
      school: updated.school ? 'pending' : 'unverified',
      firm: updated.firm ? 'pending' : 'unverified'
    });
  };

  const applyLead = (lead: typeof VERIFIED_LEADS[0]) => {
    setSelectedLead(lead);
    setInvestor({
      ...investor,
      name: lead.name,
      firm: lead.firm,
      school: lead.school as any,
      investorType: lead.type as any,
      source1: lead.source,
      verificationNotes: `Sourced from ${lead.source}. Requires verification of LinkedIn and email.`
    });
    setActiveTab('add');
  };

  const canSubmit = () => {
    return (
      investor.name &&
      investor.firm &&
      investor.linkedin &&
      investor.school &&
      investor.investorType &&
      investor.source1 &&
      investor.source2
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending': return <AlertCircle className="w-4 h-4 text-amber-500" />;
      default: return <XCircle className="w-4 h-4 text-red-500" />;
    }
  };

  return (
    <div className="bg-background rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-slate-900 to-slate-700 p-6">
        <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
          <Shield className="w-6 h-6 text-green-400" />
          Verified Investor Data Collection
        </h2>
        <p className="text-gray-300 mt-1">
          Add new investors with verified contact information per protocol standards
        </p>
      </div>

      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('add')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'add' 
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Add New Investor
        </button>
        <button
          onClick={() => setActiveTab('leads')}
          className={`px-6 py-3 text-sm font-medium transition-colors ${
            activeTab === 'leads' 
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Verified Leads ({VERIFIED_LEADS.length})
        </button>
      </div>

      <div className="p-6">
        {activeTab === 'add' ? (
          <div className="space-y-6">
            {/* Verification Status Bar */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(verification).map(([key, status]) => (
                <Badge 
                  key={key}
                  variant={status === 'verified' ? 'default' : status === 'pending' ? 'secondary' : 'destructive'}
                  className="flex items-center gap-1 capitalize"
                >
                  {getStatusIcon(status)}
                  {key}
                </Badge>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <Input
                  value={investor.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., James J. Shannon"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Role
                </label>
                <Input
                  value={investor.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  placeholder="e.g., Chief Executive Officer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firm/Employer *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={investor.firm}
                    onChange={(e) => handleInputChange('firm', e.target.value)}
                    placeholder="e.g., Indus Capital Partners"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firm Website
                </label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={investor.firmWebsite}
                    onChange={(e) => handleInputChange('firmWebsite', e.target.value)}
                    placeholder="https://www.induscap.com"
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  LinkedIn URL *
                </label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={investor.linkedin}
                    onChange={(e) => handleInputChange('linkedin', e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (Verified)
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={investor.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="name@firm.com"
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Must match firm domain. No generic emails (gmail, info@, etc.)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Affiliation *
                </label>
                <div className="relative">
                  <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={investor.school}
                    onChange={(e) => handleInputChange('school', e.target.value)}
                    className="w-full h-10 pl-10 pr-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select School</option>
                    <option value="Northeastern University">Northeastern University</option>
                    <option value="St. John's University">St. John&apos;s University</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Investor Type *
                </label>
                <select
                  value={investor.investorType}
                  onChange={(e) => handleInputChange('investorType', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Type</option>
                  <option value="VC">Venture Capital</option>
                  <option value="Angel">Angel Investor</option>
                  <option value="PE">Private Equity</option>
                  <option value="Family Office">Family Office</option>
                </select>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Verification Sources (Required: 2+)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Primary Source *
                  </label>
                  <Input
                    value={investor.source1}
                    onChange={(e) => handleInputChange('source1', e.target.value)}
                    placeholder="e.g., Huntington Angels Network Alumni Directory"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secondary Source *
                  </label>
                  <Input
                    value={investor.source2}
                    onChange={(e) => handleInputChange('source2', e.target.value)}
                    placeholder="e.g., LinkedIn Profile + Crunchbase"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verification Notes
              </label>
              <textarea
                value={investor.verificationNotes}
                onChange={(e) => handleInputChange('verificationNotes', e.target.value)}
                placeholder="Document verification methodology, date accessed, and any discrepancies..."
                rows={3}
                className="w-full px-3 py-2 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-3">
              <Button 
                disabled={!canSubmit()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Add Verified Investor
              </Button>
              <Button 
                variant="outline"
                onClick={() => setInvestor(INITIAL_INVESTOR)}
              >
                Clear Form
              </Button>
            </div>

            {!canSubmit() && (
              <p className="text-sm text-amber-600">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Fill in all required fields (*) to add investor
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Pre-identified leads from verified sources. Click "Use Lead" to populate the form.
            </p>
            <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto">
              {VERIFIED_LEADS.map((lead, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-muted transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{lead.name}</p>
                    <p className="text-sm text-gray-600">{lead.firm}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs">
                        {lead.school}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {lead.type}
                      </Badge>
                      <Badge variant="outline" className="text-xs text-blue-600">
                        {lead.source}
                      </Badge>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => applyLead(lead)}
                  >
                    Use Lead
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

